import menuItems, { findItemByKeyPath, findItemKeyByPath } from './menuItems'
import { faChevronDoubleLeft, faChevronDoubleRight, faChevronDown, faCog, faPlus, faSparkles } from '@fortawesome/pro-solid-svg-icons'
import { faSignOut, faUser } from "@fortawesome/pro-duotone-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, ConfigProvider, Dropdown, Flex, Menu, Row, Space, ThemeConfig, Typography } from "antd"
import theme from 'antd/lib/theme'
import { useSavedState } from "../../hooks/useSavedState"
import { NavigationService } from "../../services/navigationService"
import { useSignOutMutation } from "../authentication/authenticationQueries"
import VSpace from "../common/VSpace"
import './Sidebar.css'
import { useContext, useEffect, useState } from "react"
import AccountSetupModal from '../AccountConfig/AccountSetupModal'
import AddNewModal from './AddNewModal'
import { AuthenticationContext } from "../../AuthenticationContext"
import SynkedUPPaymentsNotification from "../invoicing/SynkedUPPaymentsNotification"
import { useInvoicingSettingsQuery } from "../../domain/invoicing/settings/invoicingSettingsQueries"
import AiAssistantDrawer from '../AiAssistant/AiAssistantDrawer'
import { shouldShowAiAssistant } from './sidebarModel'
import useServiceTicketsEnabled from '../../hooks/useServiceTicketsEnabled'

const { Text } = Typography
const TOKEN_NAME = 'synkedup_token'

export type Props = {
  navigationService: NavigationService
}

const Sidebar = ({ navigationService }: Props) => {
  const { currentUser } = useContext(AuthenticationContext)
  const [collapsed, setCollapsed] = useSavedState('collapsed', false)
  const [expandedSubmenu, setExpandedSubmenu] = useSavedState('expandedSubmenu', 'information')
  const invoicingSettingsQuery = useInvoicingSettingsQuery()
  const hasAccountingRole = currentUser?.roles?.includes('Accounting') ?? false
  const hasInvoicingPermission = currentUser?.permissionKeys.includes('allow_invoicing') ?? false
  const showPaymentsLink = invoicingSettingsQuery.data?.invoicingSystem === 'synkedUp' && hasAccountingRole && hasInvoicingPermission

  const showServiceTickets = useServiceTicketsEnabled()

  const items = menuItems(currentUser, collapsed, showPaymentsLink, showServiceTickets)

  const signOutMutation = useSignOutMutation()

  const [addNewModalOpen, setAddNewModalOpen] = useState(false)
  const [configModalOpen, setConfigModalOpen] = useState(false)
  const hasAiAssistant = shouldShowAiAssistant(currentUser)
  const [aiDrawerOpen, setAiDrawerOpen] = useState(false)

  const selectedKey = findItemKeyByPath(items, navigationService.state.current.baseUrl)?.key

  const toggleCollapsed = () => {
    setCollapsed(!collapsed)
  }

  useEffect(() => {
    const newSelectedKey = findItemKeyByPath(items, navigationService.state.current.baseUrl)?.key

    if (newSelectedKey && newSelectedKey !== selectedKey) {
      setTimeout(() => {
        const newExpandedSubmenu = items.find(x => x.children?.find(y => y.key === newSelectedKey))?.key

        if (newExpandedSubmenu) {
          setExpandedSubmenu(newExpandedSubmenu)
        }
      }, 10)
    }
  }, [navigationService.path, currentUser])

  useEffect(() => {
    document.body.classList.toggle('collapsed', collapsed)
  }, [collapsed])

  const sidebarTheme: ThemeConfig = {
    algorithm: theme.darkAlgorithm,
    components: {
      Menu: {
        darkItemBg: '#24344b',
        darkSubMenuItemBg: '#24344b',
        darkItemSelectedBg: '#3d587f',
        darkItemHoverBg: '#3d587f',
        darkItemColor: 'white',
        itemBorderRadius: 0,
        itemMarginInline: 0,
        itemMarginBlock: 0,
        itemPaddingInline: 4,
        collapsedWidth: 50,
      },
      Button: {
        defaultGhostBorderColor: 'white',
        defaultGhostColor: 'white',
      }
    }
  }

  const onOpenChange = (keys: string[]) => {
    let key = keys[0]
    if (keys.length > 1) {
      key = keys.filter(x => x !== expandedSubmenu)[0]
    }
    setExpandedSubmenu(key)
  }

  const handleSelect = (keyPath: string[]) => {
    const item = findItemByKeyPath(items, keyPath)
    if (!item) {
      return
    }

    if (item.url) {
      window.open(item.url, '_blank')
      return
    }

    if (item.path) {
      navigationService.setUrl(item.path)
    }
  }

  const navigateToAccount = () => {
    navigationService.setLocation('/account/details')
  }

  const handleSignOut = () => {
    signOutMutation.mutate()
    localStorage.removeItem(TOKEN_NAME)
    window.Intercom('shutdown')
    window.location.href = "/#!/account"
    window.location.reload()
  }

  return <div className="sidebar new-sidebar">
    <ConfigProvider theme={sidebarTheme}>
      <Flex vertical gap={16} justify="space-between" style={{ width: '100%', height: '100vh', overflowY: 'auto', overflowX: 'hidden' }}>
        <VSpace size="large">
          <Row justify="end" style={{ width: '100%' }}>
            <Button
              type="text"
              style={{ color: 'white', marginLeft: 'auto', marginTop: 8, marginRight: collapsed ? 'auto' : 8 }}
              icon={<FontAwesomeIcon icon={collapsed ? faChevronDoubleRight : faChevronDoubleLeft} />} onClick={toggleCollapsed}
            />
          </Row>
          <Row justify="center" style={{ width: '100%' }}>
            <div
              className="avatar"
              style={{
                backgroundImage: currentUser?.avatar ? `url(${currentUser?.avatar}?id=${Math.random()})` : undefined,
              }}
              onClick={navigateToAccount}
            />
          </Row>
          <Row justify="center" style={{ width: '100%' }}>
            <Dropdown
              placement="bottomLeft"
              menu={{
                items: [
                  { key: 'account', label: 'Account Details', icon: <FontAwesomeIcon icon={faUser} />, onClick: navigateToAccount },
                  { key: 'sign-out', label: 'Sign Out', icon: <FontAwesomeIcon icon={faSignOut} />, onClick: handleSignOut }
                ]
              }}
            >
              <Button
                type="text"
                style={{ color: 'white' }}
                icon={<FontAwesomeIcon icon={faUser} />}
              >
                <Space>
                  {!collapsed && currentUser?.displayName}
                  <FontAwesomeIcon icon={faChevronDown} size="xs" />
                </Space>
              </Button>
            </Dropdown>
          </Row>
          <div style={{ paddingInline: 8, width: '100%' }}>
            {!currentUser?.tenant?.accountConfigCompleted && <Button
              shape={collapsed ? "circle" : "round"}
              ghost
              block={!collapsed}
              icon={<FontAwesomeIcon icon={faCog} />}
              onClick={() => setConfigModalOpen(true)}
              style={{ marginBottom: 8 }}
            >
              {!collapsed && 'SETUP'}
            </Button>}
            <Button
              shape={collapsed ? "circle" : "round"}
              ghost
              block={!collapsed}
              icon={<FontAwesomeIcon icon={faPlus} />}
              onClick={() => setAddNewModalOpen(true)}
            >
              {!collapsed && 'NEW'}
            </Button>
          </div>

          <Menu
            items={items}
            mode="inline"
            theme="dark"
            inlineIndent={collapsed ? undefined : 0}
            openKeys={[expandedSubmenu]}
            selectedKeys={selectedKey ? [selectedKey] : []}
            onOpenChange={onOpenChange}
            onSelect={({ keyPath }) => handleSelect(keyPath)}
            onClick={({ keyPath }) => handleSelect(keyPath)}
            inlineCollapsed={collapsed}
          />
        </VSpace>
        <div style={{ padding: 16 }}>
          {hasAiAssistant && <Button
            shape="round"
            block
            icon={<FontAwesomeIcon icon={faSparkles} />}
            style={{
              background: 'linear-gradient(135deg, #f47458, #f4a261)',
              border: 'none',
              color: 'white',
              fontWeight: 600,
              marginBottom: 8,
            }}
            onClick={() => setAiDrawerOpen(true)}
          >
            {!collapsed && 'AI Assistant'}
          </Button>}
          {!collapsed && <Text type="secondary" style={{ fontSize: 11, textWrap: 'nowrap' }}>v {window.env.VERSION}</Text>}
        </div>
      </Flex>
    </ConfigProvider>
    <AddNewModal open={addNewModalOpen} onClose={() => setAddNewModalOpen(false)} navigationService={navigationService} />
    <AccountSetupModal
      open={configModalOpen}
      onClose={() => setConfigModalOpen(false)}
    />
    <SynkedUPPaymentsNotification />
    {hasAiAssistant && <AiAssistantDrawer open={aiDrawerOpen} onClose={() => setAiDrawerOpen(false)} />}
  </div>
}

export default Sidebar
